import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, phoneMask } from "../../lib/api";
import { useStore } from "../../context/StoreContext";
import { Logo } from "./Logo";
import { Checkbox } from "../ui/checkbox";

export default function WelcomeGate() {
  const { settings, grantAccess } = useStore();
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const started = useRef(Date.now());

  useEffect(() => {
    started.current = Date.now();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Informe seu telefone completo com DDD.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/customers/access", {
        phone: digits, promo_consent: consent, website, elapsed_ms: Date.now() - started.current,
      });
      grantAccess(data.token, data.customer);
      toast.success("Seja bem-vindo à EMAIA PERFUMES");
    } catch (err) {
      setError(errMsg(err, "Não foi possível liberar o acesso."));
    } finally {
      setLoading(false);
    }
  };

  const bg = settings?.welcome_bg ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${settings.welcome_bg}` : "/images/welcome.jpg";

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-12" data-testid="welcome-gate">
      <img src={bg} alt="" className="absolute inset-0 h-full w-full scale-105 object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#0B0B0C]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.12),transparent_60%)]" />
      <form
        onSubmit={submit}
        className="fade-up relative z-10 w-full max-w-md rounded-2xl border border-[#D4AF37]/25 bg-[#0B0B0C]/75 p-8 text-center shadow-[0_20px_60px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-10"
        data-testid="welcome-gate-form"
      >
        <div className="mb-8 flex justify-center"><Logo settings={settings} size="lg" /></div>
        <div className="gold-line mx-auto mb-8 w-32" />
        <h1 className="font-serif text-3xl font-medium text-[#FAF6F0] sm:text-4xl">{settings?.welcome_title || "Bem-vindo"}</h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#A1A1AA]">{settings?.welcome_text}</p>

        <label htmlFor="gate-phone" className="eyebrow mt-8 block text-left">Telefone com DDD</label>
        <input
          id="gate-phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="(11) 99999-9999"
          value={phone}
          onChange={(e) => setPhone(phoneMask(e.target.value))}
          className="mt-2 w-full rounded-xl border border-[#D4AF37]/30 bg-black/50 px-5 py-4 text-center text-lg tracking-wider text-[#FAF6F0] outline-none transition-[border-color,box-shadow] placeholder:text-[#A1A1AA]/50 focus:border-[#D4AF37] focus:shadow-[0_0_0_3px_rgba(212,175,55,0.15)]"
          data-testid="phone-gate-input"
        />
        <input
          type="text" name="website" tabIndex={-1} autoComplete="off" value={website}
          onChange={(e) => setWebsite(e.target.value)} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true"
        />
        {error && <p className="mt-3 text-left text-sm text-red-400" data-testid="phone-gate-error">{error}</p>}

        <label className="mt-5 flex cursor-pointer items-start gap-3 text-left text-xs leading-relaxed text-[#A1A1AA]">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-[#D4AF37]/60" data-testid="phone-gate-promo-checkbox" />
          <span>Aceito receber novidades e ofertas da EMAIA PERFUMES pelo WhatsApp. (Opcional — não é necessário para acessar o catálogo.)</span>
        </label>

        <button type="submit" disabled={loading} className="btn-gold mt-7 w-full py-4" data-testid="phone-gate-submit-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Acessar catálogo
        </button>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-[#A1A1AA]/80">
          <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />
          Seus dados são protegidos conforme a LGPD.{" "}
          <Link to="/privacidade" className="underline decoration-[#D4AF37]/50 underline-offset-2 hover:text-[#F3E5AB]" data-testid="phone-gate-privacy-link">Política de privacidade</Link>
        </p>
      </form>
    </div>
  );
}
